import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  MissionIcon,
  DiamondIcon,
  HourglassIcon,
  FlagIcon,
} from '../../shared/ToolbarPrimitives';

const NODE_ICONS = {
  task: MissionIcon,
  decision: DiamondIcon,
  wait: HourglassIcon,
  milestone: FlagIcon,
};

/**
 * Node type definitions for the insert menu
 */
const NODE_TYPES = [
  {
    id: 'task',
    label: 'Create Mission',
    description: 'Standard mission node',
    available: true,
  },
  {
    id: 'decision',
    label: 'Create Decision',
    description: 'Yes/No branch point',
    available: true,
  },
  {
    id: 'wait',
    label: 'Create Wait for',
    description: 'Pause until something arrives',
    available: true,
  },
  {
    id: 'milestone',
    label: 'Create Milestone',
    description: 'When source mission completes',
    available: true,
  },
];

/**
 * EdgeInsertMenu Component
 * 
 * Dropdown menu that appears when clicking the + button on an edge.
 * Allows user to select a node type to insert between two connected nodes.
 */
const EdgeInsertMenu = ({
  position, // { x, y } in screen coordinates
  onSelect, // (nodeType) => void
  onClose,
}) => {
  const menuRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Delay adding listener to prevent immediate close on the same click that opened it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true);
      document.addEventListener('click', handleClickOutside, true);
      document.addEventListener('keydown', handleEscape);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Close when canvas starts panning (trackpad or mouse drag)
  useEffect(() => {
    const handlePanStart = () => onClose();
    window.addEventListener('flowchartpanstart', handlePanStart);
    return () => window.removeEventListener('flowchartpanstart', handlePanStart);
  }, [onClose]);

  // Calculate menu position with viewport bounds checking
  const menuWidth = 180;
  const menuHeight = NODE_TYPES.length * 44 + 40; // Approximate height
  
  let left = position.x - menuWidth / 2; // Center under the button
  let top = position.y + 16; // Below the button
  
  // Keep within viewport
  if (left < 10) left = 10;
  if (left + menuWidth > window.innerWidth - 10) {
    left = window.innerWidth - menuWidth - 10;
  }
  if (top + menuHeight > window.innerHeight - 10) {
    top = position.y - menuHeight - 16; // Show above instead
  }

  const handleSelect = (nodeType) => {
    if (nodeType.available) {
      onSelect(nodeType.id);
    }
  };

  const menu = (
    <div
      ref={menuRef}
      className="edge-insert-menu"
      style={{
        position: 'fixed',
        left,
        top,
        zIndex: 10000,
      }}
    >
      <div className="edge-insert-menu__header">
        Insert on connection
      </div>
      <div className="edge-insert-menu__list">
        {NODE_TYPES.map((nodeType) => (
          <button
            key={nodeType.id}
            className={`edge-insert-menu__item ${!nodeType.available ? 'edge-insert-menu__item--disabled' : ''}`}
            onClick={() => handleSelect(nodeType)}
            disabled={!nodeType.available}
          >
            <span className="edge-insert-menu__item-icon">{React.createElement(NODE_ICONS[nodeType.id])}</span>
            <span className="edge-insert-menu__item-label">{nodeType.label}</span>
          </button>
        ))}
      </div>
    </div>
  );

  // Render via portal to escape SVG context
  return ReactDOM.createPortal(menu, document.body);
};

export default EdgeInsertMenu;

